import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Pencil, RefreshCw, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateMdx } from '../lib/markdown';
import { diffParagraphs, type ParagraphChange } from '../lib/text';
import type { Memory, MemoryConflict, MemoryNote, MemorySide, MemoryVersion } from '../lib/types';

const PRIMARY = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-40';
const SECONDARY = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg border border-softgrey text-graphite hover:border-brand/40 disabled:opacity-40';

const day = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function Sources({ version }: { version: MemoryVersion }) {
    if (!version.sources.length) return null;
    return (
        <p className="text-[12px] text-coolslate mt-3">
            Drawn from{' '}
            {version.sources.map((s, i) => (
                <span key={s.file_id}>
                    {i > 0 && ', '}
                    <Link to={`/files/${s.file_id}`} className="underline hover:text-brand-dark">{s.name}</Link>
                </span>
            ))}
        </p>
    );
}

function Body({ text }: { text: string }) {
    return <div className="prose-sm text-sm text-graphite leading-relaxed" dangerouslySetInnerHTML={{ __html: generateMdx(text) }} />;
}

function Paragraphs({ paragraphs, tone }: { paragraphs: ParagraphChange[]; tone: 'removed' | 'added' }) {
    const mark = tone === 'added' ? 'bg-emerald-50 border-l-2 border-emerald-400 pl-2' : 'bg-red-50 border-l-2 border-red-300 pl-2';
    return (
        <div className="space-y-2">
            {paragraphs.map((p, i) => (
                <div key={i} className={p.changed ? `${mark} rounded-r` : ''}><Body text={p.text} /></div>
            ))}
        </div>
    );
}

// The current and proposed texts side by side, each paragraph the other lacks highlighted.
function Comparison({ current, proposal }: { current: MemoryVersion | null; proposal: MemoryVersion }) {
    const diff = diffParagraphs(current?.body ?? '', proposal.body);
    return (
        <div className="grid gap-4 md:grid-cols-2">
            <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-coolslate mb-2">Now</p>
                {current ? <Paragraphs paragraphs={diff.before} tone="removed" /> : <p className="text-sm text-coolslate">Nothing yet.</p>}
            </div>
            <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-brand-dark mb-2">Proposed</p>
                <Paragraphs paragraphs={diff.after} tone="added" />
                <Sources version={proposal} />
            </div>
        </div>
    );
}

// What the next update will take in: what people asked Navaris to remember, and the
// passages it read in documents, grouped by document.
function WaitingNotes({ notes, asked }: { notes: MemoryNote[]; asked: boolean }) {
    if (!notes.length) return null;
    const people = notes.filter(n => n.kind === 'person');
    const documents = new Map<string, { name: string; passages: MemoryNote[] }>();
    for (const n of notes) {
        if (n.kind !== 'document' || !n.file_id) continue;
        const entry = documents.get(n.file_id) ?? { name: n.by ?? 'A document', passages: [] };
        entry.passages.push(n);
        documents.set(n.file_id, entry);
    }
    return (
        <div className="mb-4 rounded-lg border border-softgrey bg-offwhite p-3">
            <p className="text-[13px] font-medium text-graphite">Waiting for the next update</p>
            {people.length > 0 && (
                <ul className="mt-2 space-y-2">
                    {people.map(n => (
                        <li key={n.id} className="text-[13px] text-graphite">
                            {n.text}
                            <span className="block text-[12px] text-coolslate">
                                {n.by ? `Noted by ${n.by}, ` : 'Noted '}{day(n.created_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {[...documents].map(([fileId, d]) => (
                <details key={fileId} className="mt-2 text-[13px] text-graphite">
                    <summary className="cursor-pointer">
                        {d.passages.length === 1 ? 'One passage' : `${d.passages.length} passages`} from{' '}
                        <Link to={`/files/${fileId}`} className="underline hover:text-brand-dark">{d.name}</Link>
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-1">
                        {d.passages.map(p => <li key={p.id}>{p.text}</li>)}
                    </ul>
                </details>
            ))}
            <p className="text-[12px] text-coolslate mt-2">
                {asked ? 'Navaris is updating the memory now.'
                    : 'Navaris takes these in at its next update, or you can ask it to update now.'}
            </p>
        </div>
    );
}

const sideLabel = (side: MemorySide) =>
    side.dated ? `${side.source}, ${day(side.dated)}` : side.source.charAt(0).toUpperCase() + side.source.slice(1);

type Choice = 'kept' | 'alternative';

// Where the proposal's sources disagree, each with the side the proposal kept and the
// alternative for the person to choose.
function Conflicts({ conflicts, choices, onChoose }: {
    conflicts: MemoryConflict[];
    choices: Record<number, Choice>;
    onChoose: (index: number, choice: Choice) => void;
}) {
    return (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="flex items-center gap-1.5 text-[13px] font-medium text-graphite">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" aria-hidden />
                {conflicts.length === 1 ? 'One conflict to settle' : `${conflicts.length} conflicts to settle`}
            </p>
            <ol className="mt-2 space-y-3">
                {conflicts.map((c, i) => (
                    <li key={i}>
                        <p className="text-[12px] font-medium uppercase tracking-wide text-coolslate">{c.topic}</p>
                        {(['kept', 'alternative'] as const).map(side => {
                            const disabled = side === 'alternative' && !c.switchable;
                            return (
                                <label key={side} className={`mt-1 flex items-start gap-2 rounded-md bg-white border p-2 text-[13px] ${
                                    (choices[i] ?? 'kept') === side ? 'border-brand' : 'border-softgrey'} ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
                                    <input type="radio" name={`conflict-${i}`} className="mt-0.5" disabled={disabled}
                                           checked={(choices[i] ?? 'kept') === side} onChange={() => onChoose(i, side)} />
                                    <span>
                                        <span className="block text-[12px] text-coolslate">{sideLabel(c[side])}</span>
                                        <span className="text-graphite">{c[side].passage}</span>
                                        {disabled && <span className="block text-[12px] text-coolslate mt-1">Use Edit and accept to put this in the text.</span>}
                                    </span>
                                </label>
                            );
                        })}
                    </li>
                ))}
            </ol>
        </div>
    );
}

// The memory covering the company or an area (the area's own, else the one above it): the
// accepted version, Navaris's proposed update for a person to accept, edit or reject, and
// the person's own edit. Given `onDecided`, the panel
// serves a list of proposals: once its scope has none open it keeps showing the proposal it
// had and calls `onDecided`, so the list can let it go.
export default function MemoryPanel({ scope, onDecided, titled = false }: {
    scope: string;
    onDecided?: () => void;
    /** Head the panel with the area's name, where it sits among other areas' memories. */
    titled?: boolean;
}) {
    const { authFetch } = useAuth();
    const [text, setText] = useState<Memory | null>(null);
    const [editing, setEditing] = useState<{ body: string; proposal?: string } | null>(null);
    const [comparing, setComparing] = useState(false);
    const [choices, setChoices] = useState<Record<number, Choice>>({});
    const [asked, setAsked] = useState(false);
    const [asking, setAsking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const decided = useRef(onDecided);
    decided.current = onDecided;
    const path = `/memory/${encodeURIComponent(scope)}`;
    const reload = useCallback(() => {
        authFetch(path)
            .then(r => r.ok ? r.json() : null)
            .then((next: Memory | null) => {
                if (decided.current && !next?.proposal) decided.current();
                else setText(next);
            })
            .catch(() => setText(null));
    }, [authFetch, path]);
    useEffect(() => {
        reload();
        window.addEventListener('navaris:agent-turn-done', reload);
        return () => window.removeEventListener('navaris:agent-turn-done', reload);
    }, [reload]);

    const act = async (url: string, init: RequestInit) => {
        setBusy(true);
        const res = await authFetch(url, init).catch(() => null);
        setBusy(false);
        if (res?.ok) { setEditing(null); setComparing(false); reload(); }
        return res?.ok ?? false;
    };
    const json = (method: string, body: unknown): RequestInit => ({
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });

    const proposalId = text?.proposal?.id;
    useEffect(() => setChoices({}), [proposalId]);

    if (!text) return null;
    const { current, proposal } = text;
    const conflicts = proposal?.conflicts ?? [];
    // A memory from higher up, covering this area, starts folded to its opening lines.
    const inherited = text.area !== text.scope;
    const collapsed = inherited && !expanded;

    const decide = (proposal: string, verb: 'accept' | 'reject', init: RequestInit) =>
        act(`${path}/proposals/${proposal}/${verb}`, init);
    const save = () => {
        if (!editing) return;
        if (editing.proposal) decide(editing.proposal, 'accept', json('POST', { body: editing.body }));
        else act(path, json('PUT', { body: editing.body }));
    };
    const askNavaris = async () => {
        setAsking(true);
        const res = await authFetch(`${path}/refresh`, { method: 'POST' }).catch(() => null);
        setAsking(false);
        if (res?.ok) setAsked(true);
    };

    return (
        <section data-agent-view className="border border-softgrey rounded-xl bg-white p-4">
            <div className={`flex items-center justify-between gap-2 ${collapsed || (!titled && editing) ? '' : 'mb-2'}`}>
                {titled ? <h4 className="text-sm font-semibold text-graphite">{text.name}</h4> : <span />}
                {!editing && !collapsed && (
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={askNavaris} disabled={asking || asked}
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-coolslate hover:text-brand-dark hover:bg-offwhite disabled:opacity-50">
                            <RefreshCw className="w-3.5 h-3.5" aria-hidden />
                            {asked ? 'Navaris is updating it' : 'Ask Navaris to update'}
                        </button>
                        <button type="button" onClick={() => setEditing({ body: current?.body ?? '' })}
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-coolslate hover:text-brand-dark hover:bg-offwhite">
                            <Pencil className="w-3.5 h-3.5" aria-hidden />
                            Edit
                        </button>
                    </div>
                )}
            </div>

            {!editing && <WaitingNotes notes={text.notes ?? []} asked={asked} />}
            {collapsed ? (
                current ? (
                    <>
                        <div className="relative max-h-24 overflow-hidden">
                            <Body text={current.body} />
                            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white" />
                        </div>
                        <button type="button" onClick={() => setExpanded(true)}
                                className="mt-1 text-[12px] text-coolslate underline hover:text-brand-dark">
                            Show more
                        </button>
                    </>
                ) : (
                    <p className="text-sm text-coolslate">
                        Nothing remembered yet. Navaris writes this from your filed documents and what you ask
                        it to remember in chat, or you can write it yourself.
                    </p>
                )
            ) : <>
            {proposal && !editing && (
                <div className="mb-4 rounded-lg border border-brand/40 bg-brand/5 p-3">
                    <p className="flex items-center gap-1.5 text-[13px] font-medium text-brand-dark">
                        <Sparkles className="w-3.5 h-3.5" aria-hidden />
                        Navaris suggests an update ({day(proposal.created_at)})
                    </p>
                    {proposal.summary && <p className="text-[13px] text-graphite mt-1">{proposal.summary}</p>}
                    {conflicts.length > 0 && (
                        <Conflicts conflicts={conflicts} choices={choices}
                                   onChoose={(i, choice) => setChoices({ ...choices, [i]: choice })} />
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <button type="button" className={PRIMARY} disabled={busy}
                                onClick={() => decide(proposal.id, 'accept', json('POST', { choices }))}>
                            {conflicts.length ? 'Accept with these choices' : 'Accept'}
                        </button>
                        <button type="button" className={SECONDARY} disabled={busy}
                                onClick={() => setEditing({ body: proposal.body, proposal: proposal.id })}>
                            Edit and accept
                        </button>
                        <button type="button" className={SECONDARY} disabled={busy}
                                onClick={() => decide(proposal.id, 'reject', { method: 'POST' })}>
                            Reject
                        </button>
                        <button type="button" onClick={() => setComparing(v => !v)} aria-expanded={comparing}
                                className="text-[12px] text-coolslate underline hover:text-brand-dark">
                            {comparing ? 'Hide the proposed update' : 'See the proposed update'}
                        </button>
                    </div>
                </div>
            )}

            {editing ? (
                <div>
                    <textarea
                        value={editing.body}
                        onChange={e => setEditing({ ...editing, body: e.target.value })}
                        rows={Math.min(30, Math.max(8, editing.body.split('\n').length + 2))}
                        className="w-full border border-softgrey rounded-lg p-3 text-sm font-mono text-graphite focus:outline-none focus:border-brand"
                        aria-label={`${text.name} memory`}
                    />
                    <div className="flex items-center gap-2 mt-2">
                        <button type="button" className={PRIMARY} disabled={busy || !editing.body.trim()} onClick={save}>
                            {editing.proposal ? 'Accept with my edits' : 'Save'}
                        </button>
                        <button type="button" className={SECONDARY} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                </div>
            ) : comparing && proposal ? (
                <Comparison current={current} proposal={proposal} />
            ) : current ? (
                <>
                    {current.author === 'navaris' && !current.decided_by && (
                        <p className="text-[12px] text-coolslate mb-2">Written by Navaris from your documents. Edit it wherever it is wrong.</p>
                    )}
                    <Body text={current.body} />
                    <Sources version={current} />
                </>
            ) : (
                <p className="text-sm text-coolslate">
                    Nothing remembered yet. Navaris writes this from your filed documents and what you ask
                    it to remember in chat, or you can write it yourself.
                </p>
            )}
            {inherited && !editing && (
                <button type="button" onClick={() => setExpanded(false)}
                        className="mt-2 text-[12px] text-coolslate underline hover:text-brand-dark">
                    Show less
                </button>
            )}
            </>}
        </section>
    );
}
